import { ReactNode } from 'react';
import styled from '@emotion/styled';
import Button from './Button';

interface NoDataProps {
    /** 메인 안내 메시지 */
    message?: string;
    /** 보조 설명 (메시지 아래) */
    description?: string;
    /** 상단 일러스트/아이콘 (이모지나 SVG 모두 가능) */
    icon?: ReactNode;
    /** 액션 버튼 라벨 (있으면 표시) */
    actionLabel?: string;
    /** 액션 버튼 클릭 핸들러 */
    onAction?: () => void;
    /** 컴팩트 모드 (모달 안 등 좁은 공간) */
    compact?: boolean;
}

const Wrapper = styled.div<{ compact?: boolean }>`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    padding: ${(p) => (p.compact ? '32px 20px' : '60px 20px')};
    text-align: center;
    color: #475569;
`;

const Icon = styled.div<{ compact?: boolean }>`
    font-size: ${(p) => (p.compact ? '2rem' : '3rem')};
    line-height: 1;
    margin-bottom: 4px;
`;

const Message = styled.p<{ compact?: boolean }>`
    font-size: ${(p) => (p.compact ? '0.95rem' : '1.05rem')};
    color: #1e293b;
    margin: 0;
    font-weight: 500;
`;

const Description = styled.p`
    font-size: 0.85rem;
    color: #94a3b8;
    margin: 0;
    line-height: 1.5;
`;

const ActionWrapper = styled.div`
    margin-top: 8px;
`;

/**
 * 빈 상태 공용 element.
 * 일러스트(이모지/아이콘) + 메인 메시지 + 보조 설명 + 액션 버튼(선택).
 *
 * @example
 * <NoData
 *   icon="📋"
 *   message="등록된 채용공고가 없습니다"
 *   description="첫 채용공고를 등록하고 인재를 만나보세요"
 *   actionLabel="+ 채용공고 등록"
 *   onAction={() => navigate('/recruitments/new')}
 * />
 */
const NoData = ({
    message = '데이터가 없습니다.',
    description,
    icon = '📭',
    actionLabel,
    onAction,
    compact,
}: NoDataProps) => (
    <Wrapper compact={compact}>
        {icon && <Icon compact={compact}>{icon}</Icon>}
        <Message compact={compact}>{message}</Message>
        {description && <Description>{description}</Description>}
        {actionLabel && onAction && (
            <ActionWrapper>
                <Button variant="primary" size="medium" width="auto" onClick={onAction}>
                    {actionLabel}
                </Button>
            </ActionWrapper>
        )}
    </Wrapper>
);

export default NoData;
